/* =========================================================================
   Foxhills Cart Add-on Rail
   Conditional cross-sell rail shown in the cart drawer (and cart page).
   - Reads the live cart, classifies each line item by MATERIAL using the
     product's tags (/products/<handle>.js, since /cart.js has no tags).
   - Shows the right care add-ons (silver cloth / gold dip + cloth / dazzle
     stick) plus always-on extras (gift bag, greeting card, chocolates).
   - Config is injected by snippets/cart-addon-rail.liquid as window.FoxAddons.

   This store's cart drawer is app-overridden and does NOT emit the theme's
   updateCart.ajaxcart event, so we watch the cart DOM with a MutationObserver
   and re-render whenever the cart changes (or the rail container is recreated
   empty). A cart-signature check prevents our own writes from looping.
   Depends on: jQuery, theme.cart, theme.Currency (loaded by theme.js).
   ========================================================================= */
(function () {
  'use strict';

  var CFG = window.FoxAddons;
  if (!CFG || !CFG.enabled) return;

  // The chocolate range is configured as a raw newline/comma-separated handle
  // list (theme setting). Each handle renders only if the product is active on
  // the Online Store (draft/unpublished 404s on product.js) AND in stock.
  if (CFG.addons && CFG.addons.CHOCOLATES && CFG.addons.CHOCOLATES.handlesRaw) {
    CFG.addons.CHOCOLATES.handles = String(CFG.addons.CHOCOLATES.handlesRaw)
      .split(/[\n,]+/)
      .map(function (h) { return h.trim(); })
      .filter(Boolean);
  }

  var RAIL_ID = 'FoxAddonRail';

  // There are TWO rail containers: one in the drawer (ajax-cart-template.liquid)
  // and one on the cart page (templates/cart.liquid), and they share an id.
  // getElementById returns only the first, so the cart page's rail was never
  // filled and the upsell was invisible to anyone who went to /cart instead of
  // opening the drawer. Always write to every container.
  function railEls() {
    return Array.prototype.slice.call(document.querySelectorAll('#' + RAIL_ID));
  }
  var productCache = {};   // handle -> Promise(product.js json)
  var lastSig = null;      // signature of the last cart we rendered for
  var observer = null;
  var debounceTimer = null;

  /* ---------- helpers ---------- */

  function money(cents) {
    try {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    } catch (e) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  // routes.root_url is "/" on this store, so naive concat gives "//products/.."
  // (a protocol-relative URL). Strip the trailing slash before joining.
  var ROOT = String(CFG.routeRoot || '').replace(/\/$/, '');

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (!productCache[handle]) {
      productCache[handle] = fetch(ROOT + '/products/' + handle + '.js', {
        credentials: 'same-origin'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return productCache[handle];
  }

  function lc(arr) { return (arr || []).map(function (t) { return String(t).toLowerCase().trim(); }); }

  function anyTag(tags, list) {
    for (var i = 0; i < list.length; i++) {
      if (tags.indexOf(list[i]) !== -1) return true;
    }
    return false;
  }

  // Does any product tag loosely contain a phrase in list?
  function anyTagContains(tags, list) {
    for (var i = 0; i < tags.length; i++) {
      for (var j = 0; j < list.length; j++) {
        if (tags[i].indexOf(list[j]) !== -1) return true;
      }
    }
    return false;
  }

  // Strictly available only: a sold-out product must disappear from the rail
  // (no fallback to an unbuyable first variant).
  function firstAvailableVariant(product) {
    if (!product || !product.variants) return null;
    var avail = product.variants.filter(function (v) { return v.available; });
    return avail[0] || null;
  }

  function inCart(cart, handle) {
    return ((cart && cart.items) || []).some(function (it) { return it.handle === handle; });
  }

  function cartSig(cart) {
    return ((cart && cart.items) || []).map(function (it) {
      return it.variant_id + ':' + it.quantity;
    }).join(',');
  }

  /* ---------- classification ---------- */

  function classifyCart(cart) {
    var items = (cart && cart.items) || [];
    var jobs = items.map(function (it) {
      return fetchProduct(it.handle).then(function (p) { return { item: it, product: p }; });
    });
    return Promise.all(jobs).then(function (pairs) {
      var flags = { silver: false, gold: false, diamond: false, types: [] };
      pairs.forEach(function (pair) {
        var p = pair.product, it = pair.item;
        if (!p) return;
        // Collected before any early return below, so the gift-bag suppression
        // still sees a handbag even when it fails the cleaning-upsell rules.
        var ptype = (p.type || '').toLowerCase().trim();
        if (ptype && flags.types.indexOf(ptype) === -1) flags.types.push(ptype);
        var tags = lc(p.tags);
        var title = (p.title || '').toLowerCase();
        var price = it.price; // pence, what they're actually paying

        // Gold-finish = real gold, gold-plated or vermeil. Detect via title too:
        // plated/vermeil pieces are often tagged only by their silver base metal.
        var goldish = /\bgold\b|vermeil|plated/.test(title) ||
                      anyTagContains(tags, ['gold', 'vermeil', 'plated']);

        // Gold / plated / vermeil below the threshold get NO cleaning at all
        // (no silver cloth, no gold care) — they're not worth a cleaning upsell.
        if (goldish && price < (CFG.rules.gold ? CFG.rules.gold.minPrice : 40000)) return;

        if (goldish) {
          if (CFG.rules.gold) flags.gold = true;                 // gold-finish >= threshold -> gold dip + cloth
        } else if (CFG.rules.silver && anyTag(tags, CFG.rules.silver.tags)) {
          flags.silver = true;                                   // genuine silver -> silver cloth
        }
        if (CFG.rules.diamond && anyTag(tags, CFG.rules.diamond.tags) &&
            !anyTag(tags, CFG.rules.diamond.excludeTags || [])) {
          flags.diamond = true;
        }
      });
      return flags;
    });
  }

  /* ---------- which add-ons to offer ---------- */

  function addonsToShow(flags) {
    var keys = [];
    if (flags.silver) keys = keys.concat(CFG.rules.silver.addons);
    if (flags.gold) keys = keys.concat(CFG.rules.gold.addons);
    if (flags.diamond) keys = keys.concat(CFG.rules.diamond.addons);

    (CFG.always || []).forEach(function (k) {
      if (k === 'CHOCOLATES' && !CFG.chocolatesEnabled) return;
      keys.push(k);
    });

    var seen = {};
    return keys.filter(function (k) {
      if (seen[k] || !CFG.addons[k]) return false;
      seen[k] = true;
      return true;
    }).map(function (k) {
      var def = CFG.addons[k];
      return {
        key: k, type: def.type, handle: def.handle,
        label: def.label, price: def.price, handles: def.handles
      };
    });
  }

  /* ---------- rendering ---------- */

  function cardHtml(def, product, variant) {
    var img = product.featured_image
      ? product.featured_image.replace(/(\.[^.?]*)(\?|$)/, '_120x$1$2')
      : '//cdn.shopify.com/s/assets/admin/no-image-medium-cc9732cb976dd349a0df1d39816fbcc7.gif';
    var label = def.label || product.title;
    return '' +
      '<li class="fox-addon" data-key="' + def.key + '" data-handle="' + product.handle + '">' +
        '<img class="fox-addon__img" src="' + img + '" alt="" loading="lazy" title="Tap to see more">' +
        '<div class="fox-addon__info">' +
          '<span class="fox-addon__title">' + label + '</span>' +
          '<span class="fox-addon__price">' + money(variant.price) + '</span>' +
        '</div>' +
        '<button type="button" class="fox-addon__add" data-variant="' + variant.id + '" aria-label="Add ' + label + '">Add</button>' +
      '</li>';
  }

  function cardPickerHtml(def, products) {
    var thumbs = products.map(function (p) {
      if (!p) return '';
      var v = firstAvailableVariant(p);
      if (!v) return '';
      var img = p.featured_image ? p.featured_image.replace(/(\.[^.?]*)(\?|$)/, '_120x$1$2') : '';
      return '<button type="button" class="fox-card-pick" data-variant="' + v.id + '" title="' + p.title + '">' +
        '<img src="' + img + '" alt="' + p.title + '" loading="lazy"></button>';
    }).join('');
    if (!thumbs) return '';
    return '' +
      '<li class="fox-addon fox-addon--cards">' +
        '<div class="fox-addon__info fox-addon__info--full">' +
          '<span class="fox-addon__title">' + (def.label || 'Add a greeting card') +
            (def.price ? ' <em>' + money(def.price) + '</em>' : '') + '</span>' +
          '<div class="fox-card-picker">' + thumbs + '</div>' +
        '</div>' +
      '</li>';
  }

  /* ---------- birthstone matching set (the brief's centrepiece) ----------
     Matched against assets/birthstone-map.json rather than parsed from titles.
     Title parsing broke twice when the client retitled the catalogue (Sept 2026:
     "Sterling Silver ..." became "Silver ...", so the metal filter silently
     matched nothing and mixed gold with silver in "Complete the set"). */

  var _bsMapPromise = null;
  function loadBsMap() {
    if (_bsMapPromise) return _bsMapPromise;
    var url = window.FoxBsMapUrl;
    if (!url) return Promise.resolve(null);
    _bsMapPromise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return _bsMapPromise;
  }

  function typeFromTitle(title) {
    var t = (title || '').toLowerCase();
    if (t.indexOf('bracelet') !== -1) return 'bracelet';
    if (t.indexOf('earring') !== -1) return 'earrings';
    if (t.indexOf('bangle') !== -1) return 'bangle';
    if (t.indexOf('ring') !== -1) return 'ring';
    if (t.indexOf('necklace') !== -1 || t.indexOf('pendant') !== -1) return 'necklace';
    return 'other';
  }

  // Style range, so a 1710 Crystal piece isn't completed with a Teardrop piece.
  function rangeOfFamily(f) {
    if (!f) return '';
    if (f.indexOf('milgrain') === 0) return 'milgrain';
    if (f === 'crystal-teardrop-birthstone-necklace' || f === 'crystal-birthstone-stud-earrings') return 'teardrop';
    if (f === 'birthstone-necklace' || f.indexOf('huggie') > -1) return 'claw';
    return '1710';
  }

  function birthstoneMatches(cart) {
    var items = (cart && cart.items) || [];
    if (!items.length) return Promise.resolve([]);
    return loadBsMap().then(function (map) {
      if (!map || !map.items) return [];
      var cartHandles = items.map(function (it) { return it.handle; });
      var seed = null;
      for (var i = 0; i < items.length; i++) {
        var it = map.items[items[i].handle];
        if (it) { seed = it; break; }
      }
      if (!seed) return [];
      var seedRange = rangeOfFamily(seed.f);
      var haveTypes = items.map(function (ci) {
        var mi = map.items[ci.handle];
        return typeFromTitle(mi ? mi.t : ci.title);
      });
      var byType = {};
      Object.keys(map.items).forEach(function (h) {
        var c = map.items[h];
        if (c.mo !== seed.mo) return;                                  // same month
        if (c.mat !== seed.mat) return;                                // SAME METAL
        if (rangeOfFamily(c.f) !== seedRange) return;                  // same range
        if (c.f === seed.f) return;                                    // different style
        if (/engrav/i.test(c.f) || /engravable/i.test(c.t || '')) return;
        if (c.av === false) return;                                    // in stock only
        if (cartHandles.indexOf(h) !== -1) return;
        var pt = typeFromTitle(c.t);
        if (haveTypes.indexOf(pt) !== -1) return;                      // type already covered
        if (!byType[pt]) byType[pt] = h;
      });
      return Object.keys(byType).map(function (t) { return byType[t]; }).slice(0, 3);
    }).catch(function () { return []; });
  }

  // Build the AbbottLyon-style cart additions — birthstone "complete the set" +
  // care cross-sell list + gift-wrap banner + free-shipping bar — into the
  // (re-queried) rail element, and inject the running total into the checkout button.
  function renderRail(cart) {
    if (!cart || !cart.items) return;
    Promise.all([classifyCart(cart), birthstoneMatches(cart)]).then(function (pre) {
      var flags = pre[0], bsHandles = pre[1] || [];
      var defs = addonsToShow(flags);
      var jobs = defs.map(function (def) {
        if (def.type === 'card-picker' || def.type === 'multi') {
          return Promise.all((def.handles || []).map(fetchProduct))
            .then(function (prods) { return { def: def, products: prods.filter(Boolean) }; });
        }
        return fetchProduct(def.handle).then(function (p) { return { def: def, product: p }; });
      });
      var gwHandle = CFG.giftWrap && CFG.giftWrap.enabled ? CFG.giftWrap.handle : null;
      var gwJob = gwHandle ? fetchProduct(gwHandle) : Promise.resolve(null);
      var bsJob = Promise.all(bsHandles.map(fetchProduct));

      return Promise.all([Promise.all(jobs), gwJob, bsJob]).then(function (out) {
        var resolved = out[0], gwProduct = out[1], bsProducts = out[2];

        // 0) birthstone "complete the set"
        var setHtml = '';
        (bsProducts || []).forEach(function (p) {
          if (!p || inCart(cart, p.handle)) return;
          var v = firstAvailableVariant(p);
          if (!v) return;
          setHtml += cardHtml({ key: 'set:' + p.handle }, p, v);
        });
        var setSection = setHtml
          ? '<div class="fox-addon-rail__head">Complete the set</div><ul class="fox-addon-rail__list">' + setHtml + '</ul>'
          : '';

        // 1) split cleaning/care products from finishing-touch extras so the
        //    cleaning heading reads as care (not "complete the look").
        var CARE_KEYS = { SILVER_CLOTH: 1, GOLD_CLOTH: 1, GOLD_DIP: 1, DAZZLE: 1 };
        function listFrom(wantCare) {
          var h = '';
          resolved.forEach(function (r) {
            var isCare = !!CARE_KEYS[r.def.key];
            if (isCare !== wantCare) return;
            if (r.def.type === 'card-picker') { h += cardPickerHtml(r.def, r.products); return; }
            if (r.def.type === 'multi') {
              // one card per live product (active + published + in stock);
              // draft/unpublished never resolved, sold-out has no variant.
              (r.products || []).forEach(function (p) {
                if (!p || inCart(cart, p.handle)) return;
                var v = firstAvailableVariant(p);
                if (!v) return;
                h += cardHtml({ key: r.def.key + ':' + p.handle }, p, v);
              });
              return;
            }
            if (!r.product) return;
            if (inCart(cart, r.product.handle)) return;   // already added
            var v = firstAvailableVariant(r.product);
            if (!v) return;                                // sold out
            h += cardHtml(r.def, r.product, v);
          });
          return h;
        }
        function section(head, listHtml) {
          return listHtml
            ? '<div class="fox-addon-rail__head">' + head + '</div><ul class="fox-addon-rail__list">' + listHtml + '</ul>'
            : '';
        }
        var careSection  = section(CFG.heading || 'Keep the sparkle', listFrom(true));
        var extraSection = section(CFG.extraHeading || 'Add the finishing touch', listFrom(false));

        // Free-delivery bar removed per client (no free delivery offered).
        var html = setSection + careSection + extraSection + giftWrapHtml(cart, gwProduct, flags);

        var rails = railEls();                           // re-query: may have been recreated
        if (!rails.length) return;
        rails.forEach(function (rail) {
          if (!html) { rail.innerHTML = ''; rail.style.display = 'none'; }
          else { rail.innerHTML = html; rail.style.display = ''; }
        });

        injectCheckoutTotal(cart);
      });
    }).catch(function (e) {
      if (window.console) console.warn('[FoxAddons]', e);
    });
  }

  // AbbottLyon-style gift-wrap banner (uses the gift-bag product).
  function giftWrapHtml(cart, product, flags) {
    var gw = CFG.giftWrap;
    if (!gw || !gw.enabled || !product || inCart(cart, product.handle)) return '';
    // The bag is jewellery-sized, so it is not an upsell next to a handbag.
    var suppress = String(gw.suppressTypes || '').split(',')
      .map(function (t) { return t.toLowerCase().trim(); })
      .filter(Boolean);
    var have = (flags && flags.types) || [];
    for (var i = 0; i < suppress.length; i++) {
      if (have.indexOf(suppress[i]) !== -1) return '';
    }
    var v = firstAvailableVariant(product);
    if (!v) return '';
    var img = product.featured_image
      ? product.featured_image.replace(/(\.[^.?]*)(\?|$)/, '_160x$1$2') : '';
    return '' +
      '<div class="fox-giftwrap">' +
        '<img class="fox-giftwrap__img" src="' + img + '" alt="" loading="lazy">' +
        '<div class="fox-giftwrap__body">' +
          '<span class="fox-giftwrap__title">' + (gw.title || 'Add gift wrapping') +
            ' <em>' + money(v.price) + '</em></span>' +
          '<span class="fox-giftwrap__sub">' + (gw.sub || '') + '</span>' +
        '</div>' +
        '<button type="button" class="fox-addon__add fox-giftwrap__add" data-variant="' + v.id +
          '" aria-label="' + (gw.title || 'Add gift wrapping') + '">Add</button>' +
      '</div>';
  }

  // Free-shipping progress bar.
  function shipBarHtml(cart) {
    var fs = CFG.freeShip;
    if (!fs || !fs.enabled) return '';
    var total = cart.total_price, threshold = fs.threshold || 0;
    if (threshold > 0 && total < threshold) {
      var pct = Math.max(0, Math.min(100, Math.round(total / threshold * 100)));
      var msg = (fs.remainingMsg || 'Spend {amount} more for free shipping').replace('{amount}', money(threshold - total));
      return '<div class="fox-ship">' +
        '<div class="fox-ship__msg">' + msg + '</div>' +
        '<div class="fox-ship__track"><span class="fox-ship__fill" style="width:' + pct + '%"></span></div>' +
        '</div>';
    }
    return '<div class="fox-ship fox-ship--qualified">' +
      '<div class="fox-ship__msg">' + truckSvg() + ' ' + (fs.qualifiedMsg || 'You qualify for free shipping') + '</div>' +
      '</div>';
  }

  function truckSvg() {
    return '<svg class="fox-ship__icon" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">' +
      '<path fill="currentColor" d="M2 5h12v8H2zM14 8h4l3 3v2h-7zM7 18a1.6 1.6 0 1 1 0-3.2A1.6 1.6 0 0 1 7 18zm9 0a1.6 1.6 0 1 1 0-3.2A1.6 1.6 0 0 1 16 18z"/></svg>';
  }

  // Append the running total to the drawer checkout button (AL: "CHECKOUT — £X").
  function injectCheckoutTotal(cart) {
    if (!CFG.showCheckoutTotal) return;
    var btns = document.querySelectorAll('#CartDrawer .cart__checkout');
    Array.prototype.forEach.call(btns, function (btn) {
      var base = btn.getAttribute('data-fox-label') || (btn.textContent || 'Checkout').trim();
      btn.setAttribute('data-fox-label', base);
      btn.textContent = base + ' — ' + money(cart.total_price);
    });
  }

  /* ---------- add to cart ---------- */

  function addVariant(variantId, $btn) {
    if (!variantId) return;
    if ($btn) $btn.prop('disabled', true).addClass('is-loading');
    theme.cart.addItemFromForm('id=' + variantId + '&quantity=1')
      .then(function () {
        // rebuild + (re)open the drawer; our observer picks up the cart change
        window.jQuery('body').trigger('added.ajaxProduct');
      })
      .always(function () {
        if ($btn) $btn.prop('disabled', false).removeClass('is-loading');
      });
  }

  /* ---------- sync loop ---------- */

  // Re-render when the cart actually changed, OR when the rail was recreated
  // empty while the cart still has items (the drawer rebuild wipes our element).
  // Because our own write doesn't change the cart signature and leaves the rail
  // non-empty, it never re-triggers itself.
  function sync() {
    theme.cart.getCart().then(function (cart) {
      var sig = cartSig(cart);
      var rails = railEls();
      var railEmpty = rails.length && rails.some(function (r) { return r.innerHTML.length === 0; });
      if (sig !== lastSig || (railEmpty && cart.items.length)) {
        lastSig = sig;
        renderRail(cart);
      }
    });
  }

  function scheduleSync() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(sync, 150);
  }

  function startObserver() {
    var target = document.getElementById('CartDrawer') || document.getElementById('CartContainer');
    if (!target || observer) return;
    observer = new MutationObserver(scheduleSync);
    observer.observe(target, { childList: true, subtree: true });
  }

  // The product-page Add-to-Cart is handled by HulkApps, which adds the item
  // silently WITHOUT opening the theme's cart drawer. Watch for the add to land
  // and then open the drawer so the slider always pulls up on add.
  function watchAddToCart() {
    var $ = window.jQuery;
    document.addEventListener('click', function (ev) {
      var btn = ev.target.closest &&
        ev.target.closest('.product-single__form button[name="add"], button.add-to-cart, .add-to-cart');
      if (!btn) return;
      if (btn.closest('.shopify-payment-button')) return;  // leave "Buy it now"
      if (btn.closest('#FoxAddonRail')) return;             // rail adds already open the drawer
      theme.cart.getCart().then(function (before) {
        var baseCount = before.item_count, tries = 0;
        (function poll() {
          tries++;
          theme.cart.getCart().then(function (c) {
            if (c.item_count > baseCount) {
              $('body').trigger('added.ajaxProduct');         // rebuild + open drawer
            } else if (tries < 10) {
              setTimeout(poll, 450);
            }
          });
        })();
      });
    }, true);  // capture: fire even if HulkApps stops propagation on the button
  }

  /* ---------- greeting-card chooser modal (larger, mobile-friendly) ---------- */

  function openCardModal(focusVariant) {
    var def = CFG.addons && CFG.addons.GREETING_CARD;
    if (!def || document.getElementById('FoxCardModal')) return;
    var el = document.createElement('div');
    el.id = 'FoxCardModal';
    el.className = 'fox-cardm';
    el.innerHTML =
      '<div class="fox-cardm__overlay" data-cardclose></div>' +
      '<div class="fox-cardm__box" role="dialog" aria-label="Choose a greeting card">' +
        '<div class="fox-cardm__head"><span class="fox-cardm__title">Choose a greeting card</span>' +
          '<button type="button" class="fox-cardm__close" data-cardclose aria-label="Close">&times;</button></div>' +
        '<div class="fox-cardm__track"><div class="fox-cardm__loading">Loading cards...</div></div>' +
        '<div class="fox-cardm__foot"><button type="button" class="fox-cardm__skip" data-cardclose>No card, thanks</button></div>' +
      '</div>';
    document.body.appendChild(el);
    document.documentElement.classList.add('fox-cardm-open');
    requestAnimationFrame(function () { el.classList.add('is-open'); });

    Promise.all((def.handles || []).map(fetchProduct)).then(function (prods) {
      var slides = prods.filter(Boolean).map(function (p) {
        var v = firstAvailableVariant(p);
        if (!v) return '';
        var img = p.featured_image ? p.featured_image.replace(/(\.[^.?]*)(\?|$)/, '_600x$1$2') : '';
        return '<div class="fox-cardm__slide" data-variant="' + v.id + '">' +
          '<img class="fox-cardm__img" src="' + img + '" alt="' + p.title + '">' +
          '<div class="fox-cardm__cap">' + p.title + '</div>' +
          '<button type="button" class="fox-addon__add fox-cardm__add" data-variant="' + v.id + '">Add this card &middot; ' + money(v.price) + '</button>' +
          '</div>';
      }).join('');
      var track = el.querySelector('.fox-cardm__track');
      if (!track) return;
      track.innerHTML = slides || '<div class="fox-cardm__loading">No cards available.</div>';
      if (focusVariant) {  // open on the card the customer tapped
        var target = track.querySelector('.fox-cardm__slide[data-variant="' + focusVariant + '"]');
        if (target) target.scrollIntoView({ inline: 'center', block: 'nearest' });
      }
    }).catch(function () {});
  }

  function closeCardModal() {
    document.documentElement.classList.remove('fox-cardm-open');
    ['FoxCardModal', 'FoxProdModal'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el) return;
      el.classList.remove('is-open');
      setTimeout(function () { if (el.parentNode) el.remove(); }, 220);
    });
  }

  // Larger detail view for a rail product (cleaning products, set pieces) so the
  // customer can see it before adding.
  function openProductModal(handle) {
    if (!handle || document.getElementById('FoxProdModal')) return;
    var el = document.createElement('div');
    el.id = 'FoxProdModal';
    el.className = 'fox-cardm';
    el.innerHTML =
      '<div class="fox-cardm__overlay" data-cardclose></div>' +
      '<div class="fox-cardm__box" role="dialog" aria-label="Product detail">' +
        '<div class="fox-cardm__head"><span class="fox-cardm__title">Product detail</span>' +
          '<button type="button" class="fox-cardm__close" data-cardclose aria-label="Close">&times;</button></div>' +
        '<div class="fox-pm__body"><div class="fox-cardm__loading">Loading...</div></div>' +
      '</div>';
    document.body.appendChild(el);
    document.documentElement.classList.add('fox-cardm-open');
    requestAnimationFrame(function () { el.classList.add('is-open'); });

    fetchProduct(handle).then(function (p) {
      var body = el.querySelector('.fox-pm__body');
      if (!p || !body) { if (body) body.innerHTML = '<div class="fox-cardm__loading">Unavailable.</div>'; return; }
      var v = firstAvailableVariant(p);
      var img = p.featured_image ? p.featured_image.replace(/(\.[^.?]*)(\?|$)/, '_600x$1$2') : '';
      var desc = (p.description || '').trim();
      body.innerHTML =
        '<img class="fox-pm__img" src="' + img + '" alt="' + p.title + '">' +
        '<div class="fox-pm__title">' + p.title + '</div>' +
        (v ? '<div class="fox-pm__price">' + money(v.price) + '</div>' : '') +
        (desc ? '<div class="fox-pm__desc">' + desc + '</div>' : '') +
        (v ? '<button type="button" class="fox-addon__add fox-cardm__add" data-variant="' + v.id + '">Add to bag &middot; ' + money(v.price) + '</button>' : '');
    }).catch(function () {});
  }

  function wire() {
    var $ = window.jQuery;

    $('body').on('click', '.fox-addon__add', function () {
      var $b = $(this);
      addVariant($b.data('variant'), $b);
      if ($b.closest('.fox-cardm').length) setTimeout(closeCardModal, 60);  // added from a modal -> close it
    });
    // Tapping a rail product image opens its larger detail view (cleaning products, set pieces)
    $('body').on('click', '.fox-addon__img', function () {
      var h = $(this).closest('.fox-addon').data('handle');
      if (h) openProductModal(h);
    });
    // Tapping a card thumbnail opens the larger chooser (easier to see on mobile)
    $('body').on('click', '.fox-card-pick', function () { openCardModal($(this).data('variant')); });
    $('body').on('click', '[data-cardclose]', function () { closeCardModal(); });
    $(document).on('keydown', function (e) { if (e.key === 'Escape') closeCardModal(); });

    startObserver();
    watchAddToCart();
    sync();   // initial render (cart page + already-built drawer)
  }

  // A page-speed app defers jQuery, so deps may be missing on first run. Poll.
  (function waitForDeps(tries) {
    tries = tries || 0;
    if (window.jQuery && window.theme && window.theme.cart && window.theme.cart.getCart) {
      wire();
    } else if (tries < 150) {
      setTimeout(function () { waitForDeps(tries + 1); }, 100);
    }
  })();
})();
